import React, { useState, useEffect, useRef, useCallback, useMemo } from "react"
import { api } from "aldoo-ra/API"
import ThumbnailView from "./thumbnail-view"
import { url } from "aldoo-ra/CMS/url"

const cmsAPI = api("cms")

const THUMB_HEIGHT = 96  // h-24
const THUMB_GAP = 8      // gap-2
const ROW_HEIGHT = THUMB_HEIGHT + THUMB_GAP
const PAGE_SIZE = 50
const OVERSCAN_ROWS = 2
const PADDING = 16        // p-4 on wrapper

function getColumns(width) {
  if (width >= 1024) return 9
  if (width >= 768)  return 7
  if (width >= 640)  return 5
  return 3
}

export default function FileBrowser({
  match = {},
  onFileSelect,
  onFileConfirm,
  onToggleFile,
  onReplaceSelection,
  onClearSelection,
  selectedFile,
  selectedFiles = [],
  isVisible = true,
  refreshKey = 0,
  onPaginationChange,
}) {
  const viewportRef = useRef(null)
  const [scrollTop, setScrollTop] = useState(0)
  const [viewportSize, setViewportSize] = useState({ width: 0, height: 0 })
  const [totalItems, setTotalItems] = useState(0)
  const [items, setItems] = useState({})       // index -> file
  const loadedPages  = useRef(new Set())
  const loadingPages = useRef(new Set())
  const matchKey = useMemo(() => JSON.stringify(match), [match])

  // Drag-to-select state
  // dragStartRef: { x, y, isOnItem } — set on mousedown (capture), cleared on mouseup
  const dragStartRef  = useRef(null)
  const dragRectRef   = useRef(null)   // mirrors dragRect state for closure access
  const isDraggingRef = useRef(false)  // true once movement exceeds threshold
  const wasDragRef    = useRef(false)  // true after a drag — suppresses the subsequent click
  const [dragRect, setDragRect] = useState(null)

  const cols = getColumns(viewportSize.width - PADDING * 2)
  const cellWidth = cols > 0 && viewportSize.width > 0
    ? (viewportSize.width - PADDING * 2 - (cols - 1) * THUMB_GAP) / cols
    : 0
  const totalRows   = Math.ceil(totalItems / cols)
  const totalHeight = totalRows * ROW_HEIGHT - (totalRows > 0 ? THUMB_GAP : 0)

  const firstVisibleRow = Math.max(0, Math.floor(scrollTop / ROW_HEIGHT) - OVERSCAN_ROWS)
  const visibleRows     = Math.ceil(viewportSize.height / ROW_HEIGHT) + OVERSCAN_ROWS * 2 + 1
  const lastVisibleRow  = Math.min(totalRows - 1, firstVisibleRow + visibleRows)
  const firstIdx = firstVisibleRow * cols
  const lastIdx  = Math.min(totalItems - 1, (lastVisibleRow + 1) * cols - 1)

  // Reset and fetch first page whenever match/refreshKey changes
  useEffect(() => {
    if (!isVisible) return
    loadedPages.current  = new Set()
    loadingPages.current = new Set()
    setItems({})
    setTotalItems(0)
    setScrollTop(0)
    if (viewportRef.current) viewportRef.current.scrollTop = 0

    loadingPages.current.add(1)
    cmsAPI({
      file_list: {
        itemsPerPage: PAGE_SIZE,
        page: 1,
        match: { ...match, deleted: { $ne: true } },
        sort: { date: -1 },
      },
    }).then((res) => {
      setTotalItems(res.totalItems || 0)
      const batch = {}
      ;(res.data || []).forEach((file, i) => {
        batch[i] = { ...file, url: url(file.path) }
      })
      setItems(batch)
      loadedPages.current.add(1)
      loadingPages.current.delete(1)
    })
  }, [matchKey, refreshKey, isVisible])

  // Load pages covering the visible range
  useEffect(() => {
    if (!totalItems || cols === 0) return
    const firstPage = Math.floor(firstIdx / PAGE_SIZE) + 1
    const lastPage  = Math.floor(lastIdx  / PAGE_SIZE) + 1

    for (let page = firstPage; page <= lastPage; page++) {
      if (loadedPages.current.has(page) || loadingPages.current.has(page)) continue
      loadingPages.current.add(page)

      cmsAPI({
        file_list: {
          itemsPerPage: PAGE_SIZE,
          page,
          match: { ...match, deleted: { $ne: true } },
          sort: { date: -1 },
        },
      }).then((res) => {
        const startIdx = (page - 1) * PAGE_SIZE
        const batch = {}
        ;(res.data || []).forEach((file, i) => {
          batch[startIdx + i] = { ...file, url: url(file.path) }
        })
        setItems((prev) => ({ ...prev, ...batch }))
        loadedPages.current.add(page)
        loadingPages.current.delete(page)
      })
    }
  }, [firstIdx, lastIdx, totalItems, cols])

  // Measure viewport
  useEffect(() => {
    const el = viewportRef.current
    if (!el) return
    const observer = new ResizeObserver(([entry]) => {
      const { width, height } = entry.contentRect
      setViewportSize({ width, height })
    })
    observer.observe(el)
    return () => observer.disconnect()
  }, [])

  // Report to parent
  useEffect(() => {
    onPaginationChange?.({
      totalItems,
      visibleCount: lastIdx - firstIdx + 1,
    })
  }, [totalItems, firstIdx, lastIdx])

  const handleScroll = useCallback((e) => {
    setScrollTop(e.currentTarget.scrollTop)
  }, [])

  const isFileSelected = (file) => {
    if (!file) return false
    const list = selectedFiles.length > 0 ? selectedFiles : (selectedFile ? [selectedFile] : [])
    return list.some((f) => {
      if (f._id && file._id) return f._id === file._id
      if (f.path && file.path) return f.path === file.path
      return false
    })
  }

  // Derive selected index from the active file (last in selectedFiles)
  const activeFile = selectedFiles.length > 0 ? selectedFiles[selectedFiles.length - 1] : selectedFile
  const selectedIdx = useMemo(() => {
    if (!activeFile) return -1
    const entry = Object.entries(items).find(([, f]) => {
      if (activeFile._id && f._id) return activeFile._id === f._id
      if (activeFile.path && f.path) return activeFile.path === f.path
      return false
    })
    return entry ? parseInt(entry[0], 10) : -1
  }, [activeFile, items])

  const scrollToIndex = useCallback((idx) => {
    if (!viewportRef.current || cols === 0) return
    const row = Math.floor(idx / cols)
    const top = PADDING + row * ROW_HEIGHT
    const bottom = top + THUMB_HEIGHT
    const vp = viewportRef.current
    const st = vp.scrollTop
    const vh = vp.clientHeight
    if (top < st) vp.scrollTop = top - PADDING
    else if (bottom > st + vh) vp.scrollTop = bottom - vh + PADDING
  }, [cols])

  const navigateTo = useCallback((newIdx) => {
    if (newIdx < 0 || newIdx >= totalItems || cols === 0) return
    scrollToIndex(newIdx)
    const file = items[newIdx]
    if (file) onFileSelect?.(file)
  }, [items, cols, totalItems, scrollToIndex, onFileSelect])

  const handleViewportKeyDown = useCallback((e) => {
    if (e.key === 'Enter') {
      e.preventDefault()
      e.stopPropagation()
      const file = selectedIdx !== -1 ? items[selectedIdx] : null
      if (file) onFileConfirm?.(file)
      return
    }
    if (!['ArrowLeft', 'ArrowRight', 'ArrowUp', 'ArrowDown'].includes(e.key)) return
    e.preventDefault()
    e.stopPropagation()
    if (selectedIdx === -1) { navigateTo(0); return }
    if (e.key === 'ArrowRight') navigateTo(selectedIdx + 1)
    else if (e.key === 'ArrowLeft') navigateTo(selectedIdx - 1)
    else if (e.key === 'ArrowDown') navigateTo(selectedIdx + cols)
    else if (e.key === 'ArrowUp') navigateTo(selectedIdx - cols)
  }, [selectedIdx, cols, navigateTo, items, onFileConfirm])

  const handleFileClick = (e, file) => {
    // Suppress click that fires after a drag
    if (wasDragRef.current) {
      wasDragRef.current = false
      return
    }
    viewportRef.current?.focus({ preventScroll: true })
    if (e.shiftKey) {
      onToggleFile?.(file)
      return
    }
    if (isFileSelected(file) && selectedFiles.length <= 1) {
      onFileConfirm?.(file)
    } else {
      onFileSelect?.(file)
    }
  }

  const handleFileKeyDown = (e, file) => {
    if (e.key !== 'Enter') return
    e.preventDefault()
    e.stopPropagation()
    if (isFileSelected(file)) {
      onFileConfirm?.(file)
    } else {
      onFileSelect?.(file)
    }
  }

  // --- Drag-to-select ---

  // Capture-phase mousedown on the viewport — fires before any child stopPropagation
  useEffect(() => {
    const vp = viewportRef.current
    if (!vp) return
    const onMouseDown = (e) => {
      if (e.button !== 0) return
      const bounds = vp.getBoundingClientRect()
      const x = e.clientX - bounds.left
      const y = e.clientY - bounds.top + vp.scrollTop
      const isOnItem = !!e.target.closest('[data-file-item]')
      dragStartRef.current = { x, y, isOnItem }
      isDraggingRef.current = false
    }
    vp.addEventListener('mousedown', onMouseDown, { capture: true })
    return () => vp.removeEventListener('mousedown', onMouseDown, { capture: true })
  }, [])

  // Latest compute function (always sees current items/cols/cellWidth via closure reassignment)
  const computeIntersectingRef = useRef(null)
  computeIntersectingRef.current = (rect) => {
    if (!rect || cols === 0 || cellWidth === 0) return []
    const x1 = Math.min(rect.x1, rect.x2)
    const y1 = Math.min(rect.y1, rect.y2)
    const x2 = Math.max(rect.x1, rect.x2)
    const y2 = Math.max(rect.y1, rect.y2)
    const result = []
    for (const [idxStr, file] of Object.entries(items)) {
      const idx = parseInt(idxStr, 10)
      const row = Math.floor(idx / cols)
      const col = idx % cols
      const itemTop    = PADDING + row * ROW_HEIGHT
      const itemLeft   = PADDING + col * (cellWidth + THUMB_GAP)
      const itemBottom = itemTop + THUMB_HEIGHT
      const itemRight  = itemLeft + cellWidth
      if (!(itemRight < x1 || itemLeft > x2 || itemBottom < y1 || itemTop > y2)) {
        result.push(file)
      }
    }
    return result
  }

  // Real-time drag rect highlight
  const draggingFiles = useMemo(() => {
    if (!dragRect) return []
    return computeIntersectingRef.current?.(dragRect) ?? []
  }, [dragRect])

  const isDragFile = (file) => {
    if (!dragRect || !file) return false
    return draggingFiles.some((f) => {
      if (f._id && file._id) return f._id === file._id
      if (f.path && file.path) return f.path === file.path
      return false
    })
  }

  // Window-level drag tracking
  useEffect(() => {
    const onMouseMove = (e) => {
      if (!dragStartRef.current) return
      const vp = viewportRef.current
      if (!vp) return
      const bounds = vp.getBoundingClientRect()
      const x = e.clientX - bounds.left
      const y = e.clientY - bounds.top + vp.scrollTop
      const { x: sx, y: sy } = dragStartRef.current
      if (!isDraggingRef.current && Math.abs(x - sx) < 5 && Math.abs(y - sy) < 5) return
      if (!isDraggingRef.current) {
        wasDragRef.current = true
      }
      isDraggingRef.current = true
      const rect = { x1: sx, y1: sy, x2: x, y2: y }
      dragRectRef.current = rect
      setDragRect(rect)
    }

    const onMouseUp = () => {
      if (!dragStartRef.current) return
      const wasDragging = isDraggingRef.current
      const isOnItem = dragStartRef.current.isOnItem
      const rect = dragRectRef.current
      dragStartRef.current = null
      isDraggingRef.current = false
      dragRectRef.current = null
      setDragRect(null)

      if (wasDragging && rect) {
        const files = computeIntersectingRef.current?.(rect) ?? []
        if (files.length > 0) onReplaceSelection?.(files)
        else onClearSelection?.()
        // Reset wasDragRef after the click event fires (click fires after mouseup)
        setTimeout(() => { wasDragRef.current = false }, 0)
      } else if (!isOnItem) {
        // Plain click on background — clear selection
        onClearSelection?.()
      }
      // Plain click on item — let handleFileClick handle it
    }

    window.addEventListener('mousemove', onMouseMove)
    window.addEventListener('mouseup', onMouseUp)
    return () => {
      window.removeEventListener('mousemove', onMouseMove)
      window.removeEventListener('mouseup', onMouseUp)
    }
  }, [onReplaceSelection, onClearSelection])

  // Visual drag rect dimensions
  const dragRectStyle = dragRect ? (() => {
    const x1 = Math.min(dragRect.x1, dragRect.x2)
    const y1 = Math.min(dragRect.y1, dragRect.y2)
    return {
      left:   x1,
      top:    y1,
      width:  Math.abs(dragRect.x2 - dragRect.x1),
      height: Math.abs(dragRect.y2 - dragRect.y1),
    }
  })() : null

  // Build only visible slots
  const slots = []
  for (let i = firstIdx; i <= lastIdx; i++) {
    const row = Math.floor(i / cols)
    const col = i % cols
    const top  = PADDING + row * ROW_HEIGHT
    const left = PADDING + col * (cellWidth + THUMB_GAP)
    const file = items[i]

    slots.push(
      <div
        key={i}
        data-file-item="true"
        style={{ position: 'absolute', top, left, width: cellWidth, height: THUMB_HEIGHT }}
      >
        {file ? (
          <ThumbnailView
            file={file}
            onClick={handleFileClick}
            onKeyDown={handleFileKeyDown}
            isSelected={isFileSelected(file) || isDragFile(file)}
          />
        ) : (
          <div className="w-full h-full rounded-md bg-admin_paper animate-pulse" />
        )}
      </div>
    )
  }

  return (
    <div
      ref={viewportRef}
      className="flex-1 min-h-0 overflow-y-auto scrollbar-thin scrollbar-thumb-admin_neutral_2 scrollbar-track-admin_canvas outline-none"
      style={dragRect ? { userSelect: 'none', cursor: 'crosshair' } : undefined}
      tabIndex={0}
      onScroll={handleScroll}
      onKeyDown={handleViewportKeyDown}
      onDragStart={(e) => e.preventDefault()}
      onClick={(e) => e.stopPropagation()}
    >
      <div style={{ position: 'relative', height: totalHeight + PADDING * 2 }}>
        {totalItems === 0 && loadedPages.current.size > 0 && (
          <div className="absolute inset-0 flex items-center justify-center text-admin_text_secondary text-sm">
            No files found
          </div>
        )}
        {slots}
        {/* Drag selection rect */}
        {dragRectStyle && (
          <div
            className="pointer-events-none absolute border border-admin_primary bg-admin_primary/10"
            style={{ ...dragRectStyle, zIndex: 100 }}
          />
        )}
      </div>
    </div>
  )
}
